import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";

export default function Footer() {
  const { t, buildPath } = useI18n();

  return (
    <footer className="bg-[#0B2545] text-slate-200 border-t-4 border-[#006837]" data-testid="mexico-legal-footer">
      <div className="h-1 w-full flex" aria-hidden="true">
        <div className="flex-1 bg-[#006837]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#CE1126]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white text-[#0B2545] font-heading font-black text-lg">
              S
            </span>
            <span className="font-heading font-extrabold text-xl text-white">{t("header.brand")}</span>
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed max-w-md">{t("footer.about")}</p>

          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-[#C59B27] shrink-0" aria-hidden="true" />
              <span data-testid="footer-address">{t("footer.address")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#C59B27] shrink-0" aria-hidden="true" />
              <a href={`mailto:${t("footer.email")}`} data-testid="footer-email" className="hover:text-white">
                {t("footer.email")}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#C59B27] shrink-0" aria-hidden="true" />
              <a href={`tel:${t("footer.phone").replace(/\s/g, "")}`} data-testid="footer-phone" className="hover:text-white">
                {t("footer.phone")}
              </a>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-heading text-sm font-bold uppercase tracking-widest text-[#C59B27]">
            {t("footer.columns.company")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <Link to={buildPath("home")} data-testid="footer-home" className="hover:text-white">
                {t("footer.links.home")}
              </Link>
            </li>
            <li>
              <a href={`${buildPath("home")}#benefits`} data-testid="footer-benefits" className="hover:text-white">
                {t("footer.links.benefits")}
              </a>
            </li>
            <li>
              <a href={`${buildPath("home")}#education`} data-testid="footer-education" className="hover:text-white">
                {t("footer.links.education")}
              </a>
            </li>
            <li>
              <a href={`${buildPath("home")}#faq`} data-testid="footer-faq" className="hover:text-white">
                {t("footer.links.faq")}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-sm font-bold uppercase tracking-widest text-[#C59B27]">
            {t("footer.columns.legal")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <Link to={buildPath("privacy")} data-testid="footer-privacy" className="hover:text-white">
                {t("footer.links.privacy")}
              </Link>
            </li>
            <li>
              <Link to={buildPath("cookies")} data-testid="footer-cookies" className="hover:text-white">
                {t("footer.links.cookies")}
              </Link>
            </li>
            <li>
              <Link to={buildPath("terms")} data-testid="footer-terms" className="hover:text-white">
                {t("footer.links.terms")}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-2 gap-6 text-xs text-slate-300">
          <div>
            <p className="font-semibold text-white">{t("footer.legalName")}</p>
            <ul className="mt-3 space-y-1">
              <li><span className="text-slate-400">{t("footer.rfcLabel")}:</span> {t("footer.rfc")}</li>
              <li><span className="text-slate-400">{t("footer.licenseLabel")}:</span> {t("footer.license")}</li>
              <li><span className="text-slate-400">{t("footer.condusefLabel")}:</span> {t("footer.condusef")}</li>
              <li><span className="text-slate-400">{t("footer.jurisdictionLabel")}:</span> {t("footer.jurisdiction")}</li>
              <li><span className="text-slate-400">{t("footer.incorporationLabel")}:</span> {t("footer.incorporation")}</li>
            </ul>
          </div>
          <div>
            <p className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-[#C59B27] shrink-0" aria-hidden="true" />
              <span>{t("footer.disclaimer")}</span>
            </p>
            <p className="mt-4 text-slate-400" data-testid="footer-copyright">{t("footer.copyright")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
